import { get, pick } from 'lodash';
import { reject, resolve } from 'redux-optimist-promise';
import { compareProposalsByComfort, saveRecommendedTransport } from 'Utils/proposal/proposalUtils';
import { TRIP_INFORMATION_SET_KIDS } from 'Store/trip-information/kids/constants';
import { TRIP_INFORMATION_SET_ADULTS_NUMBER } from 'Store/trip-information/adults/constants';
import { UPDATE_TRIP_DATE_CRITERIA } from 'Store/constants';
import { ADD_SERVICE } from 'Store/booking/proposals/constants';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import {
  ADD_ACCOMMODATION,
  ADD_ACCOMMODATION_FROM_ALTERNATIVES,
  ADD_TRANSFER_TO_PROPOSALS,
  ADD_TRANSPORT,
  CLEAR_PROPOSALS,
  DELETE_SERVICE,
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  FETCH_SELECTED_PROPOSAL_PAYMENT,
  REMOVE_TRANSFER_FROM_PROPOSALS,
  SET_PROPOSALS,
  UPDATE_ATTENDEES,
  UPDATE_INSURANCES,
  DELETE_INSURANCES,
  PREPARE_BE,
  SAVE_DONATION_TO_PROPOSALS,
  SET_PROMO_CODE,
  REMOVE_PROMO_CODE,
  UPDATE_SERVICES,
  SWITCH_PROPOSALS_OFFERS,
} from './constants';
import {
  setTransferIncludedInProposal,
  updateProposal,
  updateTransfers,
  updateTransportError,
  updateAttendees,
} from './proposals.reducer.utils';
type State = Array<Proposal>;
const initialState: State = [];
export function proposalsReducer(
  state: State = initialState,
  action: Action<any>,
): State | null | undefined {
  switch (get(action, 'type')) {
    case PREPARE_BE:
    case resolve(FETCH_PROPOSALS):
    case resolve(FETCH_PROPOSALS_BY_ID): {
      const proposals = get(action, 'payload.proposals.proposals', [])
        .map(setTransferIncludedInProposal)
        .sort(compareProposalsByComfort);
      const errors = get(action, 'payload.proposals.errors', []);
      proposals.forEach((proposal) => {
        if (proposal.transports) {
          saveRecommendedTransport(proposal.transports, proposal.id);
        }
      });
      return [...proposals, ...errors];
    }

    case resolve(FETCH_SELECTED_PROPOSAL_PAYMENT): {
      const selectedProposalId = get(action, 'meta.payload.selectedProposalId');
      return state.map((proposal) => {
        if (proposal.id !== selectedProposalId) {
          return proposal;
        }

        return {
          ...proposal,
          households: proposal.households.map((household, index) => {
            return {
              ...household,
              paymentSchedule: action.payload.proposal.households[index].paymentSchedule,
            };
          }),
        };
      });
    }

    case reject(FETCH_PROPOSALS): {
      return [
        {
          errorCode: 500,
        } as Proposal,
      ];
    }

    case SET_PROPOSALS:
      return get(action, 'payload.proposals');

    case resolve(ADD_ACCOMMODATION):
      return action.payload.addAccommodation || state;

    case resolve(ADD_ACCOMMODATION_FROM_ALTERNATIVES): {
      const updatedProposal = action.payload.addAccommodation[0];
      return state.map((proposal) => {
        if (proposal.id === updatedProposal.id) {
          return updatedProposal;
        }

        return proposal;
      });
    }

    case UPDATE_ATTENDEES:
      return updateAttendees(state, get(action, 'payload.proposal'));

    case FETCH_PROPOSALS:
    case CLEAR_PROPOSALS:
    case TRIP_INFORMATION_SET_KIDS:
    case TRIP_INFORMATION_SET_ADULTS_NUMBER:
    case UPDATE_TRIP_DATE_CRITERIA:
      return initialState;

    case resolve(ADD_TRANSFER_TO_PROPOSALS): {
      const transfersAdded = get(action, 'payload', [])
        .map((item) => get(item, 'addTransfer'))
        .flat();
      return updateTransfers(state, transfersAdded);
    }

    case resolve(REMOVE_TRANSFER_FROM_PROPOSALS): {
      const transfersRemoved = get(action, 'payload', [])
        .map((item) => get(item, 'removeTransfer'))
        .flat();
      return updateTransfers(state, transfersRemoved);
    }

    case resolve(ADD_TRANSPORT): {
      if (!action.payload.addTransport) {
        return state;
      }

      return action.payload.addTransport.map((proposal) => setTransferIncludedInProposal(proposal));
    }

    case reject(ADD_TRANSPORT):
      const transportExpiredError = get(action, 'payload.errors', []).find(
        (error) => get(error, 'statusCode') === 400,
      );
      const proposalId = get(action, 'meta.payload.proposalId');
      const errorInfo = pick(transportExpiredError, ['statusCode', 'message']);
      return transportExpiredError ? updateTransportError(state, proposalId, errorInfo) : state;

    case resolve(SET_PROMO_CODE):
    case resolve(REMOVE_PROMO_CODE):
      return state.map((proposal) => {
        if (proposal.id === get(action, 'payload.promoCodeProposal.id')) {
          return {
            ...proposal,
            price: {
              ...proposal.price,
              aggregateDiscount: get(action, 'payload.promoCodeProposal.price.aggregateDiscount'),
              aggregateDiscountPromoCode: get(
                action,
                'payload.promoCodeProposal.price.aggregateDiscountPromoCode',
              ),
              promoCode: get(action, 'payload.promoCodeProposal.price.promoCode'),
              discounts: get(action, 'payload.promoCodeProposal.price.discounts'),
              total: get(action, 'payload.promoCodeProposal.price.total'),
            },
            paymentSchedule: get(action, 'payload.promoCodeProposal.paymentSchedule'),
            attendeesPrice: get(action, 'payload.promoCodeProposal.attendeesPrice'),
          };
        }

        return proposal;
      });

    case UPDATE_INSURANCES:
      const proposalFields = get(action, 'payload.modifyInsurances.proposal');

      if (!proposalFields) {
        return state;
      }

      return updateProposal(proposalFields.id, state, (proposal) => ({
        ...proposal,
        price: proposalFields.price,
        paymentSchedule: proposalFields.paymentSchedule,
        attendeesPrice: proposalFields.attendeesPrice,
      }));

    case DELETE_INSURANCES:
      const proposalDeleteInsurances = get(action, 'payload.deleteInsurances.proposal');

      if (!proposalDeleteInsurances) {
        return state;
      }

      return updateProposal(proposalDeleteInsurances.id, state, (proposal) => ({
        ...proposal,
        price: proposalDeleteInsurances.price,
        paymentSchedule: proposalDeleteInsurances.paymentSchedule,
        attendeesPrice: proposalDeleteInsurances.attendeesPrice,
      }));

    case SAVE_DONATION_TO_PROPOSALS:
      return state.map((proposal) => ({
        ...proposal,
        price: { ...proposal.price, donation: action.payload },
        households: proposal.households.map((household) => {
          const formatAmount = (amount) =>
            formatAmountForLocale(
              amount + action.payload.value,
              proposal.originalLocale || '',
              proposal.price.currency,
              {
                precision: 2,
              },
            );

          return {
            ...household,
            paymentSchedule: household.paymentSchedule
              ? {
                  ...household.paymentSchedule,
                  ...(household.paymentSchedule?.depositAmount
                    ? {
                        depositAmount: {
                          ...household.paymentSchedule.depositAmount,
                          formattedValue: formatAmount(
                            household.paymentSchedule.depositAmount.value,
                          ),
                        },
                      }
                    : {}),
                  total: {
                    ...household.paymentSchedule.total,
                    formattedValue: formatAmount(household.paymentSchedule.total.value),
                  },
                }
              : household.paymentSchedule,
          };
        }),
      }));

    case resolve(ADD_SERVICE):
    case resolve(UPDATE_SERVICES):
    case resolve(DELETE_SERVICE): {
      const data = Object.values(action.payload ?? {})[0] as
        | ReadonlyArray<Partial<Proposal>>
        | undefined;
      if (!data) {
        return state;
      }
      return state.map((proposal) => {
        const proposalData = data.find(({ id }) => id === proposal.id);

        if (proposalData) {
          return { ...proposal, ...proposalData };
        }

        return proposal;
      });
    }

    case resolve(SWITCH_PROPOSALS_OFFERS):
      return state.map((proposal) => {
        const proposalData = action.payload.proposals.find(({ id }) => id === proposal.id);

        if (proposalData) {
          return { ...proposal, ...proposalData };
        }

        return { ...proposal };
      });

    default:
      return state;
  }
}
